import { AppState } from './app';
import * as constants from '../constants';
import { addToSSObject, replaceUmlauts } from '../utils/react';

// We type the payload cast inside each switch case:
export type LocationsActionType = {
  type:
    | typeof constants.CLEAR_LOCATIONS
    | typeof constants.UPDATE_CITY_LOCATION
    | typeof constants.ADD_LOCATION
    | typeof constants.UPDATE_LOCATION
    | typeof constants.FETCH_LOCATION_POLYGONAL_BORDER
    | typeof constants.REMOVE_LOCATION
    | typeof constants.UPDATE_MAP_CENTER
    | typeof constants.HIGHLIGHT_LOCATION
    | typeof constants.TOGGLE_DRAW_RADIUS
    | typeof constants.UPDATE_LOCATION_J3;

  payload: any;
};

export const locationsReducer = (state: AppState, action: LocationsActionType, initialUserDataEC: any) => {
  switch (action.type) {
    case constants.CLEAR_LOCATIONS: {
      let newUserDataEC = JSON.parse((window as any).sessionStorage.getItem('userDataEC')) || initialUserDataEC;

      newUserDataEC.locations = {};

      (window as any).sessionStorage.setItem('userDataEC', JSON.stringify(newUserDataEC));

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          locations: {},
        },
      };
    }

    case constants.UPDATE_CITY_LOCATION: {
      let payload = { ...action.payload };
      let locations = { ...state.userDataEC.locations };
      let location = locations[replaceUmlauts(payload.address)];

      if (payload.newAddress) {
        locations[replaceUmlauts(payload.newAddress)] = { ...location };
        locations[replaceUmlauts(payload.address)] && delete locations[replaceUmlauts(payload.address)];
        location = locations[replaceUmlauts(payload.newAddress)];
        location.address = payload.newAddress;
        location.lngLtd = payload.lngLtd;
      }

      location['urn'] = `${location.context}:${location.address}:${payload.lngLtd?.lng}:${payload.lngLtd?.lat}`;

      if (location['circle'] && location['circle'].radiusUnit) {
        location['circle'] = {
          radius: payload?.circle?.radius ? payload?.circle?.radius : 1000,
          radiusUnit: location['circle'].radiusUnit,
        };
      }
      locations[replaceUmlauts(location.address)] = location;

      addToSSObject('userDataEC', 'locations', locations);

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          locations: locations,
          shouldCreateNewBasketJ2: true,
          shouldCreateNewBasketJ1: true,
          postSelectResponseElements: null,
        },
      };
    }

    case constants.ADD_LOCATION: {
      let newLocation = { ...action.payload };
      let isLocality;
      let isSublocality;

      const radius = newLocation.radius ? newLocation.radius : 1000;
      const radiusUnit = newLocation.radiusUnit ? newLocation.radiusUnit : 'm';

      if (newLocation.types) {
        const types = typeof newLocation.types === 'string' ? newLocation.types : (newLocation.types || []).join('-');
        isLocality = types.includes('locality');
        isSublocality = types.includes('sublocality');
      }

      if (isLocality || isSublocality) {
        newLocation['circle'] = {
          radius: isSublocality ? radius : isLocality ? null : radius,
          radiusUnit: isSublocality ? radiusUnit : isLocality ? null : radiusUnit,
        };
      } else {
        newLocation['circle'] = {
          radius: newLocation.radius,
          radiusUnit: newLocation.radiusUnit,
        };
      }

      newLocation['urn'] =
        `${newLocation.context}:${newLocation.address}:${newLocation.lngLtd?.lng}:${newLocation.lngLtd?.lat}`;

      let newLocations = {
        ...state.userDataEC.locations,
        [replaceUmlauts(newLocation.address)]: newLocation,
      };

      addToSSObject('userDataEC', 'locations', newLocations);
      addToSSObject('userDataEC', 'mapCenter', action.payload.lngLtd);

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          locations: newLocations,
          mapCenter: action.payload.lngLtd,
          shouldCreateNewBasketJ2: true,
        },
      };
    }

    case constants.UPDATE_LOCATION: {
      let payload = { ...action.payload };

      let locations = { ...state.userDataEC.locations };
      let location = locations[replaceUmlauts(payload.address)];
      location['circle'] = {
        radius: payload.radius,
        radiusUnit: payload.radiusUnit,
      };
      locations[replaceUmlauts(payload.address)] = location;
      addToSSObject('userDataEC', 'locations', locations);
      addToSSObject('userDataEC', 'mapCenter', location.lngLtd);

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          locations: locations,
          mapCenter: location.lngLtd,
          shouldCreateNewBasketJ2: true,
        },
      };
    }

    case constants.FETCH_LOCATION_POLYGONAL_BORDER: {
      let { address, data } = { ...action.payload } as any;

      let locations = { ...state.userDataEC.locations };
      let location = locations[replaceUmlauts(address)];

      if (data && data.border) {
        location.border = typeof data.border === 'string' ? JSON.parse(data.border) : data.border;
      }
      locations[replaceUmlauts(address)] = location;
      addToSSObject('userDataEC', 'locations', locations);
      addToSSObject('userDataEC', 'mapCenter', location.lngLtd);

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          locations: locations,
          mapCenter: location.lngLtd,
          shouldCreateNewBasketJ2: true,
        },
      };
    }

    case constants.REMOVE_LOCATION: {
      let newLocations = { ...state.userDataEC.locations };
      newLocations[action.payload] && delete newLocations[action.payload];
      addToSSObject('userDataEC', 'locations', newLocations);

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          locations: newLocations,
          shouldCreateNewBasketJ2: true,
        },
      };
    }

    case constants.UPDATE_MAP_CENTER: {
      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          mapCenter: action.payload,
        },
      };
    }

    case constants.HIGHLIGHT_LOCATION: {
      const targetUrn = action.payload;
      const isHighlighted = state.userDataEC.highlightedLocation === targetUrn;

      if (isHighlighted) {
        return {
          ...state,
          userDataEC: {
            ...state.userDataEC,
            highlightedLocation: null,
          },
        };
      } else {
        return {
          ...state,
          userDataEC: {
            ...state.userDataEC,
            highlightedLocation: targetUrn,
          },
        };
      }
    }

    case constants.UPDATE_LOCATION_J3: {
      let newLocations = { ...state.userDataEC.locations };
      newLocations[replaceUmlauts(action.payload.name)]['types'] = action.payload.data.types;
      // @ts-expect-error ignore wrongly types lngLtd here, legacy J3:
      newLocations[replaceUmlauts(action.payload.name)]['lngLtd'] = {};
      newLocations[replaceUmlauts(action.payload.name)]['lngLtd'].lng =
        // @ts-expect-error ignore wrongly types lngLtd here, legacy J3:
        newLocations[replaceUmlauts(action.payload.name)].longitude;
      newLocations[replaceUmlauts(action.payload.name)]['lngLtd'].lat =
        // @ts-expect-error ignore wrongly types lngLtd here, legacy J3:
        newLocations[replaceUmlauts(action.payload.name)].latitude;

      addToSSObject('userDataEC', 'locations', newLocations);

      return {
        ...state,
        userDataEC: {
          ...state.userDataEC,
          locations: newLocations,
        },
      };
    }

    case constants.TOGGLE_DRAW_RADIUS: {
      return {
        ...state,
        isDrawRadius: !state.isDrawRadius,
      };
    }
  }
};
